/**
 * Pure helpers for `handleNavigateToField` navigation in `RenderDialog.jsx`.
 * Kept separate so the merge behavior can be unit-tested without mounting React.
 */

function getActivityFromStep(step) {
  return typeof step === "string" ? step : step.activity;
}

function mergeUserDataObjects(base, patch) {
  const out = { ...(base || {}) };
  if (!patch) return out;
  Object.entries(patch).forEach(([key, value]) => {
    out[key] = value;
  });
  return out;
}

/**
 * Mirrors the "go BACK via stepStack" branch in `handleNavigateToField`.
 *
 * Returns `null` if the referenced screen isn't present in `stepStack`.
 */
export function computeBackJumpNavigateToField({
  stepStack,
  redoStack,
  currentActivity,
  userData,
  referenceScreen,
}) {
  const stepIndex = stepStack.findIndex(
    (step) => getActivityFromStep(step) === referenceScreen,
  );
  if (stepIndex === -1) return null;

  const stepsToGoBack = stepStack.length - stepIndex;

  let newStepStack = [...stepStack];
  let newRedoStack = [...redoStack];
  let accumulatedUserData = { ...(userData || {}) };

  for (let i = 0; i < stepsToGoBack; i++) {
    if (newStepStack.length === 0) break;

    if (i === 0) {
      newRedoStack.push({
        activity: currentActivity,
        userData,
      });
    }

    const prevStep = newStepStack.pop();

    if (prevStep && typeof prevStep === "object" && prevStep.userData) {
      accumulatedUserData = mergeUserDataObjects(
        accumulatedUserData,
        prevStep.userData,
      );
    }

    if (i < stepsToGoBack - 1) {
      newRedoStack.push(prevStep);
    } else {
      const targetActivity = getActivityFromStep(prevStep);

      for (let j = 0; j < newStepStack.length; j++) {
        const step = newStepStack[j];
        const stepUserData =
          typeof step === "object" && step && step.userData ? step.userData : {};
        accumulatedUserData = mergeUserDataObjects(
          accumulatedUserData,
          stepUserData,
        );
      }

      return {
        targetActivity,
        newStepStack,
        newRedoStack,
        accumulatedUserData,
      };
    }
  }

  return null;
}

/**
 * Mirrors the "go FORWARD via redoStack" branch in `handleNavigateToField`.
 *
 * Returns `null` if the referenced screen isn't present in `redoStack`.
 */
export function computeForwardJumpNavigateToField({
  stepStack,
  redoStack,
  currentActivity,
  userData,
  referenceScreen,
}) {
  const redoIndex = redoStack.findIndex(
    (step) => getActivityFromStep(step) === referenceScreen,
  );
  if (redoIndex === -1) return null;

  const stepsToGoForward = redoStack.length - redoIndex;

  let newStepStack = [...stepStack];
  let newRedoStack = [...redoStack];
  let targetUserData = { ...(userData || {}) };

  for (let i = 0; i < stepsToGoForward; i++) {
    if (newRedoStack.length === 0) break;

    if (i === 0) {
      newStepStack.push({
        activity: currentActivity,
        userData,
      });
    }

    const nextStep = newRedoStack.pop();

    if (nextStep && typeof nextStep === "object" && nextStep.userData) {
      targetUserData = mergeUserDataObjects(targetUserData, nextStep.userData);
    }

    if (i < stepsToGoForward - 1) {
      newStepStack.push(nextStep);
    } else {
      const targetActivity = getActivityFromStep(nextStep);
      return {
        targetActivity,
        newStepStack,
        newRedoStack,
        targetUserData,
      };
    }
  }

  return null;
}
